import React, { useEffect, useState } from "react";
import { Box, Typography, Chip, Divider, CircularProgress, Alert } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Users, Phone, PhoneCall, PhoneOff, PhoneMissed,
  UserCog, TrendingUp, CheckCircle2,
} from "lucide-react";
import { useColorMode } from "../../../theme/ColorModeContext";
import { useTranslation } from "react-i18next";

import axiosInstance from "../../../services/axiosInstance";

// ── Stat card ───────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, color, loading }) {
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  return (
    <Box sx={{
      p: 3, borderRadius: "18px", background: "var(--surface-card)", border: "1px solid var(--line-200)",
      boxShadow: "var(--shadow-card)", display: "flex", flexDirection: "column", gap: 2,
      transition: "box-shadow 0.2s",
      "&:hover": { boxShadow: `var(--shadow-card), 0 6px 18px ${alpha(color, 0.14)}` },
    }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: alpha(color, 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={21} color={color} />
        </Box>
        {sub && <Chip label={sub} size="small" sx={{ height: 22, fontSize: "0.6875rem", fontWeight: 600, background: alpha(color, 0.08), color, border: "none" }} />}
      </Box>
      {loading ? (
        <CircularProgress size={24} sx={{ color }} />
      ) : (
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.75rem", color: "text.primary", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {typeof value === "number" ? value.toLocaleString() : value ?? "—"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, fontSize: "0.8125rem", fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Mini metric row ──────────────────────────────────────────
function MetricRow({ label, value, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.8125rem" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: color || "text.primary", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

// ── Answer rate bar ──────────────────────────────────────────
function RateBar({ value, color }) {
  const { t } = useTranslation("admin");
  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>{t("answerRateToday")}</Typography>
        <Typography variant="caption" sx={{ color, fontWeight: 800 }}>{value}%</Typography>
      </Box>
      <Box sx={{ height: 6, borderRadius: 3, background: alpha(color, 0.12), overflow: "hidden" }}>
        <Box sx={{ height: "100%", borderRadius: 3, background: color, width: `${value}%`, transition: "width 0.8s ease" }} />
      </Box>
    </Box>
  );
}

// ── Main ────────────────────────────────────────────────────
const AdminDashboard = () => {
  const { t }    = useTranslation("admin");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    axiosInstance.get("/admin/stats")
      .then(r => setStats(r.data))
      .catch(e => setError(e.response?.data?.message || t("failedLoadStats")))
      .finally(() => setLoading(false));
  }, []);

  const glassSx = { background: "var(--surface-card)", border: "1px solid var(--line-200)", boxShadow: "var(--shadow-card)", borderRadius: "18px" };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary", mb: 0.5 }}>
          {t("platformOverview")}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("platformOverviewSub")}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Stat cards grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2.5, mb: 3 }}>
        <StatCard icon={Users}       label={t("statTotalUsers")}   value={stats?.totalUsers}          color="#4F46E5" sub={t("subStatAccounts")} loading={loading} />
        <StatCard icon={PhoneCall}   label={t("statTotalCalls")}   value={stats?.totalCalls}          color="#06B6D4" sub={t("subStatAllTime")}  loading={loading} />
        <StatCard icon={Phone}       label={t("statPhoneNumbers")} value={stats?.phoneNumbers?.total} color="#22C55E" sub={t("subStatInPool")}   loading={loading} />
        <StatCard icon={UserCog}     label={t("statSubAccounts")}  value={stats?.subAccounts?.total}  color="#F59E0B" sub={t("subStatAgents")}   loading={loading} />
      </Box>

      {/* Two-column detail cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, mb: 3 }}>

        {/* Today's calls card */}
        <Box sx={{ p: 3, ...glassSx }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "11px", background: alpha("#6366F1", 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TrendingUp size={20} color="#6366F1" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>{t("todayActivityTitle")}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("todayActivitySub")}</Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          {loading ? <CircularProgress size={20} /> : (
            <>
              <MetricRow label={t("totalCallsMetric")} value={stats?.todayCalls}    color="#6366F1" />
              <Divider sx={{ opacity: 0.4 }} />
              <MetricRow label={t("answeredMetric")}   value={stats?.todayAnswered} color="#22C55E" />
              <Divider sx={{ opacity: 0.4 }} />
              <MetricRow label={t("missedMetric")}     value={stats?.todayMissed}   color="#EF4444" />
              <Divider sx={{ opacity: 0.4 }} />
              <RateBar value={stats?.answerRateToday || 0} color="#22C55E" />
            </>
          )}
        </Box>

        {/* Phone & sub-account card */}
        <Box sx={{ p: 3, ...glassSx }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "11px", background: alpha("#F59E0B", 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CheckCircle2 size={20} color="#F59E0B" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>{t("resourceStatusTitle")}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("resourceStatusSub")}</Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          {loading ? <CircularProgress size={20} /> : (
            <>
              <MetricRow label={t("phoneNumbersInPool")}      value={stats?.phoneNumbers?.total}                                 color="#4F46E5" />
              <Divider sx={{ opacity: 0.4 }} />
              <MetricRow label={t("reservedNumbers")}         value={stats?.phoneNumbers?.reserved}                              color="#F59E0B" />
              <Divider sx={{ opacity: 0.4 }} />
              <MetricRow label={t("availableNumbers")}        value={(stats?.phoneNumbers?.total || 0) - (stats?.phoneNumbers?.reserved || 0)} color="#22C55E" />
              <Divider sx={{ opacity: 0.4 }} />
              <MetricRow label={t("totalSubAccountsMetric")}  value={stats?.subAccounts?.total}    color="#06B6D4" />
              <Divider sx={{ opacity: 0.4 }} />
              <MetricRow label={t("activeSubAccountsMetric")} value={stats?.subAccounts?.active}   color="#22C55E" />
            </>
          )}
        </Box>
      </Box>

      {/* Info banner */}
      <Box sx={{ p: 2.5, borderRadius: "14px", background: alpha("#DC2626", 0.05), border: `1px solid ${alpha("#DC2626", 0.15)}`, display: "flex", alignItems: "center", gap: 2 }}>
        <PhoneOff size={18} color="#DC2626" style={{ flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem" }}>
          {t("platformAdminBanner")}
        </Typography>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
